import React from 'react';
import {
  Link,
  getRouteApi,
  useLoaderData,
} from '@tanstack/react-router';

import DashboardMemoryCard from './MemoryCard';
import Pagination from '../Pagination';
import PaginationHeader from '../PaginationHeader';

import type { Memory } from '../../types/memory';

interface DashboardMemoriesProps {
  style?: 'default' | 'short';
};

const DashboardMemories: React.FC<DashboardMemoriesProps> = ({ style = 'default' }) => {
  let user;
  try {
    const routeApi = getRouteApi('/dashboard/admin/users/$userId');
    const { user: loadedUser } = routeApi.useLoaderData();
    user = loadedUser;
  } catch (error) {
    if (error instanceof Error) {
      user = undefined;
    }
  }

  const { memories: memoriesData } = useLoaderData({ strict: false });
  const memories = memoriesData?.data || [];
  const totalMemories = memoriesData?.meta.total;
  const mode = memoriesData?.meta.mode || 'created';

  return (
    <>
      {style !== 'short' && (
        <div className="dashboard-header page-header">
          {mode === 'received' ? (
            <>
              <h2 className="page-title">Received <em>Memories</em></h2>
              <p>All Memories that have been shared with you.</p>
            </>
          ) : (
            <>
              {user ? (
                <>
                  <h2 className="page-title">User’s <em>Memories</em></h2>
                  <p>Viewing Memories for {user.name} ({user.email})</p>
                </>
              ) : (
                <>
                  <h2 className="page-title">My <em>Memories</em></h2>
                  <p>Every Memory you’ve captured—whether it stands alone or belongs to a larger story.</p>
                </>
              )}
            </>
          )}
        </div>
      )}
      <div className="dashboard-memories wa-stack wa-gap-0 inner">
        <div className="wa-grid">
          {(memories.length > 0 || style === 'short') && (
            <div className="page-eyebrow">
              Standalone Memories
            </div>
          )}
          {totalMemories > 0 && (
            <div className="total-items">
              {style === 'short' ? (
                <Link to="/dashboard/memories">
                  View all Memories ({totalMemories})
                </Link>
              ) : (
                <PaginationHeader text="Memory" total={totalMemories} />
              )}
            </div>
          )}
        </div>
        {memories.length === 0 && (
          <div className="empty-state wa-stack wa-gap-md">
            <div className="center">
              {user ? (
                <>
                  This user hasn't created any Memories yet.
                </>
              ) : (
                <>
                  {mode === 'received' ? (
                    <>
                      You haven't received any Memories yet.
                    </>
                  ) : (
                    <>
                      You don't have any Memories yet. Create one!
                    </>
                  )}
                </>
              )}
            </div>
            {!user && (
              <div className="center">
                {mode !== 'received' && (
                  <Link to="/memories/create?variant=memory">
                    <wa-button className="cta-button">
                      Create Memory
                    </wa-button>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
        <div className="dashboard-memories-list wa-grid" style={{ '--min-column-size': '40ch' } as React.CSSProperties}>
          {memories.map((memory: Memory) => (
            <DashboardMemoryCard key={memory.id} memory={memory} />
          ))}
        </div>
        {style !== 'short' && (
          <Pagination total={totalMemories} />
        )}
      </div>
    </>
  );
}

export default DashboardMemories;
